import React, { useMemo } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { VsiBubbleFavs } from "../styled/App.styled";
import { useVsiAppContext } from "../stores/VsiApp";
import DonutChart from "../components/DonutChart";
import VerticalBarChart from "../components/VerticalBarChart";
import { useDataWithRoute } from "../utils/hooks";

const pieDataConfig = data => {
  let lowIops = 0;
  let midIops = 0;
  let highIops = 0;
  let maxIops = 0;
  data.forEach(datum => {
    if (datum.iops < 100) {
      lowIops = lowIops + 1;
    } else if (datum.iops > 100 && datum.iops <= 500) {
      midIops = midIops + 1;
    } else if (datum.iops > 501 && datum.iops <= 1000) {
      highIops = highIops + 1;
    } else if (datum.iops > 1000) {
      maxIops = maxIops + 1;
    }
  });

  return [
    {
      name: "< 100 IOPs",
      y: parseFloat(lowIops.toFixed(2))
    },
    {
      name: "101 - 500 IOPs",
      y: parseFloat(midIops.toFixed(2))
    },
    {
      name: "501 - 1000 IOPs",
      y: parseFloat(highIops.toFixed(2))
    },
    {
      name: "> 1000 IOPs",
      y: parseFloat(maxIops.toFixed(2))
    }
  ];
};

const filterProvisioningType = data => {
  let thin = 0;
  let thick = 0;
  let compressed = 0;
  data.forEach(datum => {
    if (datum.provisioning_type === "Thin") {
      thin++;
    } else if (datum.provisioning_type === "Thick") {
      thick++;
    } else if (datum.provisioning_type === "Compressed") {
      compressed++;
    } else {
      console.error("Something went wrong.");
    }
  });

  return [
    {
      name: "Thin",
      y: thin
    },
    {
      name: "Thick",
      y: thick
    },
    {
      name: "Compressed",
      y: compressed
    }
  ];
};

const filterLatencyLuns = (data) => {
  let filteredData = data.map(host => {
    if (host.latency === 0 || !host.latency) {
      return { title: "No latency", lunCount: host.luns_attached };
    } else if (host.latency > 0 && host.latency <= 1) {
      return { title: "0.1 - 1ms", lunCount: host.luns_attached };
    } else if (host.latency > 1 && host.latency <= 2) {
      return { title: "1.1 - 2ms", lunCount: host.luns_attached };
    } else if (host.latency > 2) {
      return { title: "Over 2ms", lunCount: host.luns_attached };
    } else {
      console.error("Not a number");
      return null;
    }
  });

  let noLat = 0;
  let lowLat = 0;
  let midLat = 0;
  let highLat = 0;
  filteredData.forEach(datum => {
    if (datum.title === "No latency") {
      noLat = noLat + datum.lunCount;
    } else if (datum.title === "0.1 - 1ms") {
      lowLat = lowLat + datum.lunCount;
    } else if (datum.title === "1.1 - 2ms") {
      midLat = midLat + datum.lunCount;
    } else if (datum.title === "Over 2ms") {
      highLat = highLat + datum.lunCount;
    } else {
      console.error("Something went wrong.");
    }
  });

  return [
    {
      name: "Latency",
      data: [noLat, lowLat, midLat, highLat]
    }
  ];
};

export default function EntHostSummary() {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("HOSTS");

  const latencyTypes = ["No latency", "0.1 - 1ms", "1 - 2ms", "Over 2ms"];

  const route = useMemo(() => `/api/v6/report/enterprise_host_summary_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordset }, { isLoading }] = useDataWithRoute(route);

  const { hostsPie, provPieInfo, hostsBar } = useMemo(() => {
    return {
      hostsPie: pieDataConfig(recordset) || [],
      provPieInfo: filterProvisioningType(recordset) || [],
      hostsBar: filterLatencyLuns(recordset) || {}
    };
  }, [recordset]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <Row>
      <Col xs={12} lg={4}>
        <VsiBubbleFavs>
          <DonutChart data={provPieInfo || []}
                      height={400}
                      title={"Provisioning"}
                      seriesName={"Count"}
                      innerSize={80} disableLabels={true}
                      enableLegend={false} />
        </VsiBubbleFavs>
      </Col>
      <Col xs={12} lg={4}>
        <VsiBubbleFavs>
          <VerticalBarChart
            categories={latencyTypes}
            series={hostsBar}
            changeAxisTitle={true}
            yTitle1={"Count of LUNs"}
            height={400}
            axisMaxMin={{ axis1Min: 0 }}
          />
        </VsiBubbleFavs>
      </Col>
      <Col xs={12} lg={4}>
        <VsiBubbleFavs>
          <DonutChart data={hostsPie || []}
                      title={"IOPs"}
                      seriesName={"Count"}
                      height={400}
                      innerSize={80} disableLabels={true}
                      enableLegend={false} />
        </VsiBubbleFavs>
      </Col>
    </Row>
  );
}
